import { cn } from '@cobliteam/ui/utils';
import type { Header } from '@tanstack/react-table';
import type React from 'react';
import { memo } from 'react';

import { getCommonPinningStyles } from '../utils';

export interface TableHeadProps<TData> extends React.ComponentPropsWithoutRef<'th'> {
  className?: string;
  header?: Header<TData, unknown>;
  index?: number;
  alignHeader?: 'left' | 'center' | 'right';
  children: React.ReactNode;
}

const alignClasses = {
  right: 'justify-end text-right',
  center: 'm-auto flex justify-center',
  left: '',
};

const TableHead = <TData,>({ className, header, index, alignHeader, ...props }: TableHeadProps<TData>) => {
  if (!header) return null;

  const alignClass = alignClasses[alignHeader || 'left'];
  const columnSize = header.column.getSize();
  const columnMeta = header.column.columnDef.meta as { minWidth?: number } | undefined;
  const minWidth = columnMeta?.minWidth ?? (columnSize === Number.MAX_SAFE_INTEGER ? 'auto' : columnSize);

  return (
    <th
      style={{
        width: columnSize === Number.MAX_SAFE_INTEGER ? 'auto' : columnSize,
        minWidth,
        ...getCommonPinningStyles<TData>(header.column),
      }}
      className={cn('group bg-gray-3 px-4 align-middle', header?.column?.getIsResizing() && 'cursor-col-resize', className)}
      {...props}
    >
      <div className={cn('flex h-full w-full items-center justify-between', alignClass)}>
        {props.children ? props.children : null}
        {header.column.getCanResize() ? (
          <div className='w-3 flex-shrink-0'>
            <div
              onMouseDown={header.getResizeHandler()}
              onTouchStart={header.getResizeHandler()}
              className={cn(
                'resizer mx-1 hidden h-5 w-1.5 animate-slide-left-and-fade cursor-col-resize rounded-md bg-gray-6 group-hover:block',
                header.column.getIsResizing() ? 'isResizing block' : ''
              )}
            />
          </div>
        ) : null}
      </div>
    </th>
  );
};

TableHead.displayName = 'TableHead';

export default memo(TableHead);
